import * as THREE from 'three'
import React, { useMemo, useState } from 'react'

import { controls } from '../../../../../../redux/reducers/common/eventReducer';
import { const_var, params } from '../../../../../../redux/reducers/reducer';

import { CSG } from '../../../../../../assets/libs/csg-lib';
import { delModel, extrudeSettings, roofPattern } from '../../../../../../utils/Functions';
import useMouseEvents from '../../../../../../hooks/useMouseEvents';
import { MaterialInnerWall, MaterialOuterWallNew, MaterialWainScotWallNew } from '../../../../CustomMaterial';

const WallModel = () => {
    const [frontWallCloseState, setFrontWallCloseState] = useState(0);
    const [backWallCloseState, setBackWallCloseState] = useState(0);
    const [sideWallCloseState, setSideWallCloseState] = useState(0);
    const [sideWallLeftPorchSize, setSideWallLeftPorchSize] = useState(0);
    const [sideWallRightPorchSize, setSideWallRightPorchSize] = useState(0);

    const wallThickness = 0.005;
    const cbFrontBackWallOffset = 0.16;
    const isEqualHeight = (params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) == params.p_h) ? 0.2 : 0

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 && !params.p_v_w) ? true : false
    
    let patternLength = params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) + (((params.p_w_c_n && params.p_b_c_b_l_f === 'Close') ? -2.935 : 0) + (params.p_r_s === 1 ? 3 : 0))
    let patternDepth = params.lean_p_w
    let verticalHorizontalPos = {"x": params.p_w / -2  - params.lean_p_w,"y" : (params.p_w_c_n && params.p_b_c_b_l_f === 'Close' ? 2.935 : 0), "z" : params.lean_p_d / 2 + 0.16}
    let verticalHorizontalRot = {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
    let lengthPattern = 3
    let depthPattern = patternDepth

    const dutchDelModelFront = useMemo(() => {
        const freeSpace = 1;
        let cornerFreeSpaceW = (params.lean_p_w - freeSpace) / 6;
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.lean_p_h - ((params.b_l_t_r_p / 12) * params.lean_p_w) :  params.lean_p_h
        let cornerFreeSpaceH = (leftHeight - freeSpace) / 6
        if (cornerFreeSpaceH > freeSpace) cornerFreeSpaceH = freeSpace * 2;

        if (cornerFreeSpaceW > freeSpace) cornerFreeSpaceW = freeSpace * 2;
        
        if (params.add_left_lean && (params.p_b_c_b_l_f === "Half Dutch End" || params.p_b_c_b_l_f === "Half End")) {
            const model = new THREE.Shape();
            model.moveTo(-(params.lean_p_w / 2 - freeSpace), 0);

            if (params.p_b_c_b_l_f === "Half Dutch End") {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
                model.lineTo(-(params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
            } else {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
            }
            model.lineTo((params.lean_p_w / 2 - freeSpace), 0);

            return model;
        } else {
            return []
        }
    }, [params.add_left_lean, params.lean_p_w, params.lean_p_h, params.p_b_c_b_l_f])

    const dutchDelModelBack = useMemo(() => {
        const freeSpace = 1;
        let cornerFreeSpaceW = (params.lean_p_w - freeSpace) / 6;
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.lean_p_h - ((params.b_l_t_r_p / 12) * params.lean_p_w) :  params.lean_p_h
        let cornerFreeSpaceH = (leftHeight - freeSpace) / 6
        if (cornerFreeSpaceH > freeSpace) cornerFreeSpaceH = freeSpace * 2;

        if (cornerFreeSpaceW > freeSpace) cornerFreeSpaceW = freeSpace * 2;
        
        if (params.add_left_lean && (params.p_b_c_b_l_b === "Half Dutch End" || params.p_b_c_b_l_b === "Half End")) {
            const model = new THREE.Shape();
            model.moveTo(-(params.lean_p_w / 2 - freeSpace), 0);

            if (params.p_b_c_b_l_b === "Half Dutch End") {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
                model.lineTo(-(params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
            } else {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
            }
            model.lineTo((params.lean_p_w / 2 - freeSpace), 0);

            return model;
        } else {
            return []
        }
    }, [params.add_left_lean, params.lean_p_w, params.lean_p_h, params.p_b_c_b_l_b])
    
    // left Lean front wall - CSG
    const frontWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l_f == 'Open') return;
        let outerModel;
        let innerModel;
        let wainscotModel;

        const frontWallModel = new THREE.Shape();

        if (params.p_b_c_b_l_f !== 'Open' || typeof(params.p_b_c_b_l_f) === 'object') {
            if (params.p_r_s === 1) {
                frontWallModel.moveTo(-(params.lean_p_w - 0.005), frontWallCloseState);
                frontWallModel.lineTo(-(params.lean_p_w - 0.005), params.lean_p_h);
                frontWallModel.lineTo(-(params.lean_p_w - 0.005), params.lean_p_h + const_var.roofRegularDst);
                frontWallModel.quadraticCurveTo(-(params.lean_p_w), params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10, 
                                                -(params.lean_p_w - const_var.roofRegularDst + 0.1), params.lean_p_h + const_var.roofRegularDst * (2 + params.b_l_t_r_p / 12))
                frontWallModel.lineTo(0, params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w) + const_var.roofRegularDst * (2)));
                frontWallModel.lineTo(0, frontWallCloseState);
                frontWallModel.closePath();
            } else {
                frontWallModel.moveTo(-params.lean_p_w + 0.005, params.lean_p_h);
                frontWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p - isEqualHeight);
                frontWallModel.lineTo(0, frontWallCloseState + (params.p_r_s === 1 && 0.1));
                frontWallModel.lineTo(-params.lean_p_w + 0.005, frontWallCloseState + (params.p_r_s === 1 && 0.1));
                frontWallModel.closePath();
            }
        };

        const frontWallCutModel = new THREE.Shape();
        if (params.p_r_s === 1) {
            frontWallCutModel.moveTo(-(params.p_w / 2 + params.lean_p_w), frontWallCloseState);
            frontWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h);
            frontWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h + const_var.roofRegularDst);
            frontWallCutModel.quadraticCurveTo( -(params.p_w / 2 + params.lean_p_w), params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10, 
                                                -(params.p_w / 2 + params.lean_p_w - const_var.roofRegularDst + 0.1), params.lean_p_h + const_var.roofRegularDst * (2 + params.b_l_t_r_p / 12))
            frontWallCutModel.lineTo(-(params.p_w / 2), params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w) + const_var.roofRegularDst * 2));
            frontWallCutModel.lineTo(-(params.p_w / 2), frontWallCloseState);
            frontWallCutModel.closePath();
        } else {
            frontWallCutModel.moveTo(-(params.p_w / 2), params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) - isEqualHeight); //right lean left top
            frontWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h); //right lean right top
            frontWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), frontWallCloseState); //right lean right bottom
            frontWallCutModel.lineTo(-(params.p_w / 2), frontWallCloseState); //right lean left bottom
            frontWallCutModel.closePath();
        }

        const meshFrontWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(frontWallModel, extrudeSettings(0.005)));
        const meshFrontDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanFront', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'green' }))
        const meshDutchDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(dutchDelModelFront, extrudeSettings(1)), new THREE.MeshStandardMaterial({color: 'blue'}))

        let meshFrontWallModel_Temp = meshFrontWallModel.clone();
        meshFrontWallModel_Temp.position.set(-params.p_w / 2, 0, params.lean_p_d / 2 + cbFrontBackWallOffset - 0.03);
        meshFrontWallModel_Temp.updateMatrixWorld();
    
        let meshFrontDelModel_Temp = meshFrontDelModel.clone();
        meshFrontDelModel_Temp.position.set(0, 0, params.lean_p_d / 2 - 0.5);
        meshFrontDelModel_Temp.updateMatrixWorld();

        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.8, 0, -params.lean_p_pos);
        meshSideDelModel_Temp.rotateY(Math.PI / 2);
        meshSideDelModel_Temp.updateMatrixWorld();

        let meshDutchDelModel_Temp = meshDutchDelModel.clone();
        meshDutchDelModel_Temp.position.set(-(params.p_w / 2 + params.lean_p_w / 2),0, params.lean_p_d / 2 - 0.5);
        meshDutchDelModel_Temp.updateMatrixWorld();
    
        const meshFrontWallModel_CSG = CSG.fromMesh(meshFrontWallModel_Temp);
        const meshFrontDelModel_CSG = CSG.fromMesh(meshFrontDelModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
        const meshDutchDelModel_CSG = CSG.fromMesh(meshDutchDelModel_Temp);
    
        const frontWallModel_CSG = meshFrontWallModel_CSG.subtract(meshFrontDelModel_CSG);
        const meshSideInnerDelModel_CSG =  frontWallModel_CSG.subtract(meshSideDelModel_CSG);
        const meshFrontInnerModel_CSG = meshSideInnerDelModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGFrontWallModel = CSG.toMesh(
            meshFrontInnerModel_CSG,
            new THREE.Matrix4()
        )
    
        innerModel = CSGFrontWallModel.geometry;

        const meshWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern), extrudeSettings(patternDepth)))  
        const meshGableDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(frontWallCutModel, extrudeSettings(1)))
    
        let meshGableDelModel_Temp = meshGableDelModel.clone();
        meshGableDelModel_Temp.position.set(0,0,params.lean_p_d / 2 - 0.5);
        meshGableDelModel_Temp.updateMatrixWorld();
    
        let meshWallModel_Temp = meshWallModel.clone();
        meshWallModel_Temp.position.set(verticalHorizontalPos.x,verticalHorizontalPos.y,verticalHorizontalPos.z);
        meshWallModel_Temp.rotation.set(verticalHorizontalRot.x,verticalHorizontalRot.y,verticalHorizontalRot.z);
        meshWallModel_Temp.updateMatrixWorld();

        let meshSideOuterDelModel_Temp = meshSideDelModel.clone();
        meshSideOuterDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.8, 0, -params.lean_p_pos);
        meshSideOuterDelModel_Temp.rotateY(Math.PI / 2);
        meshSideOuterDelModel_Temp.updateMatrixWorld();
    
        const meshWallModel_CSG = CSG.fromMesh(meshWallModel_Temp);
        const meshGableDelModel_CSG = CSG.fromMesh(meshGableDelModel_Temp);
        const meshSideOuterDelModel_CSG = CSG.fromMesh(meshSideOuterDelModel_Temp);
    
        const wallModel_CSG = meshWallModel_CSG.subtract(meshFrontDelModel_CSG);
        const GableModel_CSG = wallModel_CSG.intersect(meshGableDelModel_CSG);
        const SideOuterDelModel_CSG = GableModel_CSG.subtract(meshSideOuterDelModel_CSG);
        const MeshFrontOuterModel_CSG = SideOuterDelModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGWallModel = CSG.toMesh(MeshFrontOuterModel_CSG,new THREE.Matrix4());

        outerModel = CSGWallModel.geometry;

        /************** cutting hole in wainscot using CSG for Front***************/

        let wainscotVerticalHorizontalPos = {"x": params.p_w / -2  - params.lean_p_w,"y" : 0, "z" : params.lean_p_d / 2 + 0.16}
        let wainscotVerticalHorizontalRot = {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
        let patternDepthWS = params.lean_p_w
        let lengthPatternWS = 3
        let depthPatternWS = patternDepthWS
        
        const meshFrontWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternWS,isPattern && params.wainscotTexture == "none"), extrudeSettings(depthPatternWS)))
        let meshFrontWainscotModel_Temp = meshFrontWainscotModel.clone();
        meshFrontWainscotModel_Temp.position.set(wainscotVerticalHorizontalPos.x,wainscotVerticalHorizontalPos.y,wainscotVerticalHorizontalPos.z);
        meshFrontWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRot.x,wainscotVerticalHorizontalRot.y,wainscotVerticalHorizontalRot.z);
        meshFrontWainscotModel_Temp.updateMatrixWorld();

        const meshFrontWainscotModel_CSG = CSG.fromMesh(meshFrontWainscotModel_Temp);

        const frontWainscotModel_CSG = meshFrontWainscotModel_CSG.subtract(meshFrontDelModel_CSG);
        const sideDelWainscotModel_CSG = frontWainscotModel_CSG.subtract(meshSideOuterDelModel_CSG);
        const finalFrontwainscotModel_CSG = sideDelWainscotModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGFrontWainscotModel = CSG.toMesh(
            finalFrontwainscotModel_CSG,
            new THREE.Matrix4()
            )

        wainscotModel = CSGFrontWainscotModel.geometry;

        return {outerModel, innerModel, wainscotModel, frontWallModel, frontWallCutModel};

    }, [params.wallTexture,params.wainscotTexture,params.p_h, params.p_w, params.p_r_p, verticalHorizontalPos, verticalHorizontalRot, lengthPattern, params.lean_p_pos, depthPattern, patternLength, patternDepth, params.p_w, params.lean_p_d, params.p_b_c_b_l_f, params.p_r_s, params.lean_p_w, params.lean_p_h, params.b_l_t_r_p, frontWallCloseState, const_var.newDoorsArray, params.p_w_c_n, isPattern]);    

    // left Lean back wall - CSG
    const backWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l_b == 'Open') return;
        let outerModel;
        let innerModel;
        let wainscotModel;

        const backWallModel = new THREE.Shape();

        if (params.p_b_c_b_l_b !== 'Open' || typeof(params.p_b_c_b_l_b) === 'object') {
            if (params.p_r_s === 1) {
                backWallModel.moveTo(-(params.lean_p_w - 0.005), backWallCloseState);
                backWallModel.lineTo(-(params.lean_p_w - 0.005), params.lean_p_h);
                backWallModel.lineTo(-(params.lean_p_w - 0.005), params.lean_p_h + const_var.roofRegularDst);
                backWallModel.quadraticCurveTo(-(params.lean_p_w), params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10, 
                                                -(params.lean_p_w - const_var.roofRegularDst + 0.1), params.lean_p_h + const_var.roofRegularDst * (2 + params.b_l_t_r_p / 12))
                backWallModel.lineTo(0, params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w) + const_var.roofRegularDst * (2)));
                backWallModel.lineTo(0, backWallCloseState);
                backWallModel.closePath();
            } else {
                backWallModel.moveTo(-params.lean_p_w + 0.005, params.lean_p_h);
                backWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p - isEqualHeight);
                backWallModel.lineTo(0, backWallCloseState + (params.p_r_s === 1 && 0.1));
                backWallModel.lineTo(-params.lean_p_w + 0.005, backWallCloseState + (params.p_r_s === 1 && 0.1));
                backWallModel.closePath();
            }
        }

        const backWallCutModel = new THREE.Shape();
        if (params.p_r_s === 1) {
            backWallCutModel.moveTo(-(params.p_w / 2 + params.lean_p_w), backWallCloseState);
            backWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h);
            backWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h + const_var.roofRegularDst);
            backWallCutModel.quadraticCurveTo( -(params.p_w / 2 + params.lean_p_w), params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10, 
                                                -(params.p_w / 2 + params.lean_p_w - const_var.roofRegularDst + 0.1), params.lean_p_h + const_var.roofRegularDst * (2 + params.b_l_t_r_p / 12))
            backWallCutModel.lineTo(-(params.p_w / 2), params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w) + const_var.roofRegularDst * (2)));
            backWallCutModel.lineTo(-(params.p_w / 2), backWallCloseState);
            backWallCutModel.closePath();
        } else {
            backWallCutModel.moveTo(-(params.p_w / 2), params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) - isEqualHeight); //right lean left top
            backWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h); //right lean right top
            backWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), backWallCloseState); //right lean right bottom
            backWallCutModel.lineTo(-(params.p_w / 2), backWallCloseState); //right lean left bottom
            backWallCutModel.closePath();
        }
        
        const meshBackWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(backWallModel, extrudeSettings(0.005)));
        const meshBackDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanBack', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'green' }))
        const meshDutchDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(dutchDelModelBack, extrudeSettings(1)), new THREE.MeshStandardMaterial({color: 'blue'}))

        let meshBackWallModel_Temp = meshBackWallModel.clone();
        meshBackWallModel_Temp.position.set(-params.p_w / 2, 0, -params.lean_p_d / 2 - cbFrontBackWallOffset + 0.03);
        meshBackWallModel_Temp.updateMatrixWorld();
    
        let meshBackDelModel_Temp = meshBackDelModel.clone();
        meshBackDelModel_Temp.position.set(0, 0, -params.lean_p_d / 2 - 0.5);
        meshBackDelModel_Temp.updateMatrixWorld();

        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.8, 0, -params.lean_p_pos);
        meshSideDelModel_Temp.rotateY(Math.PI / 2);
        meshSideDelModel_Temp.updateMatrixWorld();

        let meshDutchDelModel_Temp = meshDutchDelModel.clone();
        meshDutchDelModel_Temp.position.set(-(params.p_w / 2 + params.lean_p_w / 2),0, - params.lean_p_d / 2 - 0.5);
        meshDutchDelModel_Temp.updateMatrixWorld();

        const meshBackWallModel_CSG = CSG.fromMesh(meshBackWallModel_Temp);
        const meshBackDelModel_CSG = CSG.fromMesh(meshBackDelModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
        const meshDutchDelModel_CSG = CSG.fromMesh(meshDutchDelModel_Temp);

        const backWallModel_CSG = meshBackWallModel_CSG.subtract(meshBackDelModel_CSG);
        const meshSideInnerDelModel_CSG =  backWallModel_CSG.subtract(meshSideDelModel_CSG);
        const meshBackInnerModel_CSG = meshSideInnerDelModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGBackWallModel = CSG.toMesh(
            meshBackInnerModel_CSG,
            new THREE.Matrix4()
        )
    
        innerModel = CSGBackWallModel.geometry;

        let verticalHorizontalPosB = {"x": params.p_w / -2,"y" : ((params.p_w_c_n && params.p_b_c_b_l_b === 'Close') ? 2.935 : 0), "z" : params.lean_p_d / -2 - 0.16}
        let verticalHorizontalRotB = {"x":0,"y" : Math.PI / -2,"z" : Math.PI / 2}
        let patternLengthB = params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) + ((params.p_w_c_n && params.p_b_c_b_l_b === 'Close') ? -2.935 : 0) + (params.p_r_s === 1 ? 3 : 0);
        let patternDepthB = params.lean_p_w

        const meshWallModelB = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthB,isPattern), extrudeSettings(patternDepthB)))  
        const meshGableDelModelB = new THREE.Mesh(new THREE.ExtrudeGeometry(backWallCutModel, extrudeSettings(1)))
    
        let meshGableDelModel_TempB = meshGableDelModelB.clone();
        meshGableDelModel_TempB.position.set(0,0,params.lean_p_d / -2 - 0.5);
        meshGableDelModel_TempB.updateMatrixWorld();
    
        let meshWallModel_TempB = meshWallModelB.clone();
        meshWallModel_TempB.position.set(verticalHorizontalPosB.x,verticalHorizontalPosB.y,verticalHorizontalPosB.z);
        meshWallModel_TempB.rotation.set(verticalHorizontalRotB.x,verticalHorizontalRotB.y,verticalHorizontalRotB.z);
        meshWallModel_TempB.updateMatrixWorld();

        let meshSideOuterDelModel_Temp = meshSideDelModel.clone();
        meshSideOuterDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.8, 0, -params.lean_p_pos);
        meshSideOuterDelModel_Temp.rotateY(Math.PI / 2);
        meshSideOuterDelModel_Temp.updateMatrixWorld();
    
        const meshWallModel_CSGB = CSG.fromMesh(meshWallModel_TempB);
        const meshGableDelModel_CSGB = CSG.fromMesh(meshGableDelModel_TempB);
        const meshSideOuterDelModel_CSG = CSG.fromMesh(meshSideOuterDelModel_Temp);
        
    
        const wallModel_CSGB = meshWallModel_CSGB.subtract(meshBackDelModel_CSG);
        const GableModel_CSGB = wallModel_CSGB.intersect(meshGableDelModel_CSGB);
        const SideOuterDelModel_CSG = GableModel_CSGB.subtract(meshSideOuterDelModel_CSG);
        const MeshFrontOuterModel_CSG = SideOuterDelModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGWallModelB = CSG.toMesh(MeshFrontOuterModel_CSG,new THREE.Matrix4());

        outerModel = CSGWallModelB.geometry;

            /************** cutting hole in wainscot using CSG for Back***************/
        let patternDepthBWS = params.lean_p_w
        let wainscotVerticalHorizontalPosB = {"x": params.p_w / -2,"y" : 0, "z" : params.lean_p_d / -2 - 0.16}
        let wainscotVerticalHorizontalRotB = {"x":0,"y" : Math.PI / -2,"z" : Math.PI / 2}
        let lengthPatternB = 3
        let depthPatternB = patternDepthBWS

        const meshBackWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternB,isPattern && params.wainscotTexture == "none"), extrudeSettings(depthPatternB)))
        let meshBackWainscotModel_Temp = meshBackWainscotModel.clone();
        meshBackWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosB.x,wainscotVerticalHorizontalPosB.y,wainscotVerticalHorizontalPosB.z);
        meshBackWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotB.x,wainscotVerticalHorizontalRotB.y,wainscotVerticalHorizontalRotB.z);
        meshBackWainscotModel_Temp.updateMatrixWorld();

        const meshBackWainscotModel_CSG = CSG.fromMesh(meshBackWainscotModel_Temp);

        const backWainscotModel_CSG = meshBackWainscotModel_CSG.subtract(meshBackDelModel_CSG);
        const sideDelWainscotModel_CSG = backWainscotModel_CSG.subtract(meshSideOuterDelModel_CSG);
        const finalFrontwainscotModel_CSG = sideDelWainscotModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGBackWainscotModel = CSG.toMesh(
            finalFrontwainscotModel_CSG,
            new THREE.Matrix4()
            )

            wainscotModel = CSGBackWainscotModel.geometry;

        return {innerModel, outerModel, wainscotModel}
    }, [params.leftLeanStoragePosition, params.wallTexture,params.wainscotTexture,frontWallCloseState, params.p_v_w, params.p_h, params.p_w, params.p_r_p, params.lean_p_pos, cbFrontBackWallOffset, params.p_b_c_b_l_b, params.p_r_s, params.lean_p_w, params.lean_p_d, params.b_l_t_r_p, params.lean_p_h, backWallCloseState, const_var.newDoorsArray, params.p_w_c_n, isPattern]);

    const storageWallModel = useMemo(() => {
        const model = new THREE.Shape();
        model.moveTo(-params.lean_p_d / 2, 0);
        model.lineTo(-params.lean_p_d / 2 + parseInt(params.add_storage), 0);
        model.lineTo(-params.lean_p_d / 2 + parseInt(params.add_storage), params.lean_p_h);
        model.lineTo(-params.lean_p_d / 2, params.lean_p_h);
        model.closePath();

        return model;
    }, [params.lean_p_d, params.add_storage, params.lean_p_h])
    
    // left Lean side wall - CSG
    const sideWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l == 'Open' && !params.add_storage_check) return;
        let innerModel;
        let outerModel;
        let wainscotModel;

        const sideWallModel = new THREE.Shape();

        sideWallModel.moveTo(-(params.lean_p_d / 2 + cbFrontBackWallOffset), (sideWallCloseState));
        sideWallModel.lineTo(-(params.lean_p_d / 2 + cbFrontBackWallOffset), sideWallCloseState);
        sideWallModel.lineTo(-(params.lean_p_d / 2 + sideWallLeftPorchSize + cbFrontBackWallOffset), sideWallCloseState);
        sideWallModel.lineTo(-(params.lean_p_d / 2 + sideWallLeftPorchSize + cbFrontBackWallOffset), params.lean_p_h + (params.p_r_s === 1 && 0.1));
        sideWallModel.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset) + sideWallRightPorchSize, params.lean_p_h + (params.p_r_s === 1 && 0.1));
        sideWallModel.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset) + sideWallRightPorchSize, sideWallCloseState);
        sideWallModel.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset), sideWallCloseState);
        sideWallModel.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset), (sideWallCloseState));
        sideWallModel.closePath();
        
        let lengthR = params.lean_p_d / 2
        let lengthL = params.lean_p_d / 2

        if(params.add_left_front_lean_porch && !params.add_left_back_lean_porch){
            lengthR = params.lean_p_d / 2 + params.lean_p_w
        } else if(!params.add_left_front_lean_porch && params.add_left_back_lean_porch){
            lengthL = params.lean_p_d / 2 + params.lean_p_w
        } else if(params.add_left_front_lean_porch && params.add_left_back_lean_porch){
            lengthL = params.lean_p_d / 2 + params.lean_p_w
            lengthR = params.lean_p_d / 2 + params.lean_p_w
        }

        let wallpanelValue = (sideWallCloseState < 0) ? params.lean_p_h + (params.p_r_s === 1 && 0.1) : sideWallCloseState
        const sideWallCutModel = new THREE.Shape();
        sideWallCutModel.moveTo(lengthR, params.lean_p_h + (params.p_r_s === 1 && 0.1));
        sideWallCutModel.lineTo(-(lengthL), params.lean_p_h + (params.p_r_s === 1 && 0.1));
        sideWallCutModel.lineTo(-(lengthL), wallpanelValue);
        sideWallCutModel.lineTo(lengthR, wallpanelValue);
        
        const meshSideWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(sideWallModel, extrudeSettings(0.005)));
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
        const meshStoragewallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageWallModel, extrudeSettings(1)) , new THREE.MeshStandardMaterial({color: 'blue'}))
    
        let meshSideWallModel_Temp = meshSideWallModel.clone();
        meshSideWallModel_Temp.rotateY(Math.PI / 2);
        meshSideWallModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w + 0.03, 0, 0);
        meshSideWallModel_Temp.updateMatrixWorld();

        let meshStoragewallModel_Temp = meshStoragewallModel.clone();
        meshStoragewallModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w + 0.5 , 0, params.leftLeanStoragePosition);
        meshStoragewallModel_Temp.rotation.set(0,Math.PI / -2,0)
        meshStoragewallModel_Temp.updateMatrixWorld();
    
        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.rotateY(Math.PI / 2)
        // meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w -0.5, 0, alignPosOffset);
        meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w -0.5, 0, -params.lean_p_pos);
        meshSideDelModel_Temp.updateMatrixWorld();
    
        const meshSideWallModel_CSG = CSG.fromMesh(meshSideWallModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
        const meshStoragewallModel_CSG = CSG.fromMesh(meshStoragewallModel_Temp);
    
        const sideWallModel_CSG = meshSideWallModel_CSG.subtract(meshSideDelModel_CSG);
        let meshStoragewall_CSG;

        if(params.add_storage_check) {
            meshStoragewall_CSG = sideWallModel_CSG.subtract(meshStoragewallModel_CSG)
        } else {
            meshStoragewall_CSG = sideWallModel_CSG
        }

        const CSGSideWallModel = CSG.toMesh(
            meshStoragewall_CSG,
            new THREE.Matrix4()
        )

        innerModel = CSGSideWallModel.geometry;
    
        let verticalDepth = params.lean_p_d
        let horizontalDepth = params.lean_p_d
        let VSPosition = params.lean_p_d / 2
        let HSPosition = params.lean_p_d / -2
        if(params.add_left_front_lean_porch && !params.add_left_back_lean_porch){
            VSPosition = params.lean_p_d / 2 + params.lean_p_w
            verticalDepth = params.lean_p_d + params.lean_p_w
            horizontalDepth = params.lean_p_d +  params.lean_p_w
        } else if(!params.add_left_front_lean_porch && params.add_left_back_lean_porch){
            VSPosition = params.lean_p_d / 2
            HSPosition = params.lean_p_d / -2 -  params.lean_p_w
            verticalDepth = params.lean_p_d +  params.lean_p_w
            horizontalDepth = params.lean_p_d +  params.lean_p_w
        } else if(params.add_left_front_lean_porch && params.add_left_back_lean_porch){
            VSPosition = params.lean_p_d / 2 + params.lean_p_w
            HSPosition = params.lean_p_d / -2 - params.lean_p_w
            verticalDepth = params.lean_p_d +  params.lean_p_w + params.lean_p_w
            horizontalDepth = params.lean_p_d +  params.lean_p_w + params.lean_p_w
        }
    
        let verticalHorizontalPosS = {"x": params.p_w / -2  - params.lean_p_w,"y" : ((params.p_w_c_n && (params.p_b_c_b_l === 'Close' || params.add_storage_check)) ? 2.935 : 0), "z" : HSPosition}
        let verticalHorizontalRotS = {"x":0,"y" : 0,"z" : Math.PI / 2}
        let patternLengthS = params.lean_p_h + ((params.p_w_c_n && (params.p_b_c_b_l === 'Close' || params.add_storage_check)) ? -2.935 : 0) + (params.p_r_s === 1 ? 0.1 : 0)
        let patternDepthS = horizontalDepth
        const meshSideWallModelPattern = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthS,isPattern), extrudeSettings(patternDepthS)));
        const meshWallDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(sideWallCutModel, extrudeSettings(1)))
        
        let meshWallModel_TempS = meshSideWallModelPattern.clone();
        meshWallModel_TempS.position.set(verticalHorizontalPosS.x,verticalHorizontalPosS.y,verticalHorizontalPosS.z);
        meshWallModel_TempS.rotation.set(verticalHorizontalRotS.x,verticalHorizontalRotS.y,verticalHorizontalRotS.z);
        meshWallModel_TempS.updateMatrixWorld();
    
    
        let meshWallDelModel_TempS = meshWallDelModel.clone();
        meshWallDelModel_TempS.position.set(-params.p_w / 2 - params.lean_p_w + 0.5 , 0, 0);
        meshWallDelModel_TempS.rotation.set(0,Math.PI / -2,0)
        meshWallDelModel_TempS.updateMatrixWorld();
    
        const meshWallModel_CSGS = CSG.fromMesh(meshWallModel_TempS);
        const meshWallDelModel_CSGS = CSG.fromMesh(meshWallDelModel_TempS);
    
    
        const wallModel_CSGS = meshWallModel_CSGS.subtract(meshSideDelModel_CSG);
        const meshWallDel_CSGS = wallModel_CSGS.intersect(meshWallDelModel_CSGS);
        let meshStorageOuterwall_CSG;

        if(params.add_storage_check) {
            meshStorageOuterwall_CSG = meshWallDel_CSGS.subtract(meshStoragewallModel_CSG)
        } else {
            meshStorageOuterwall_CSG = meshWallDel_CSGS
        }
        const CSGWallModelS = CSG.toMesh(meshStorageOuterwall_CSG,new THREE.Matrix4());

        outerModel = CSGWallModelS.geometry;

            /************** cutting hole in wainscot using CSG for Side***************/
        let patternDepthSWS = horizontalDepth
        let wainscotVerticalHorizontalPosS = {"x": params.p_w / -2  - params.lean_p_w,"y" : 0, "z" : HSPosition}
        let wainscotVerticalHorizontalRotS = {"x":0,"y" : 0,"z" : Math.PI / 2}
        let lengthPatternS = 3
        let depthPatternS = patternDepthSWS

        const meshSideWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternS,isPattern && params.wainscotTexture == "none"), extrudeSettings(depthPatternS)))
        let meshSideWainscotModel_Temp = meshSideWainscotModel.clone();
        meshSideWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosS.x,wainscotVerticalHorizontalPosS.y,wainscotVerticalHorizontalPosS.z);
        meshSideWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotS.x,wainscotVerticalHorizontalRotS.y,wainscotVerticalHorizontalRotS.z);
        meshSideWainscotModel_Temp.updateMatrixWorld();

        const meshSideWainscotModel_CSG = CSG.fromMesh(meshSideWainscotModel_Temp);

        const SideWainscotModel_CSG = meshSideWainscotModel_CSG.subtract(meshSideDelModel_CSG);
        const SideWainscotModelCSG = SideWainscotModel_CSG.intersect(meshWallDelModel_CSGS);
        let meshStorageWianscotwall_CSG;

        if(params.add_storage_check) {
            meshStorageWianscotwall_CSG = SideWainscotModelCSG.subtract(meshStoragewallModel_CSG)
        } else {
            meshStorageWianscotwall_CSG = SideWainscotModelCSG
        }
        const CSGSideWainscotModel = CSG.toMesh(
            meshStorageWianscotwall_CSG,
            new THREE.Matrix4()
            )

        wainscotModel = CSGSideWainscotModel.geometry;

        return {innerModel, outerModel, wainscotModel}

    }, [params.wallTexture,params.wainscotTexture,params.lean_p_pos, params.leftLeanStoragePosition, params.p_v_w, params.p_w, params.lean_p_w, params.p_r_s, params.lean_p_d, sideWallLeftPorchSize, params.lean_p_h, sideWallRightPorchSize, sideWallCloseState, params.add_storage_check, params.add_storage, params.add_left_front_lean_porch, params.add_left_back_lean_porch, const_var.newDoorsArray,params.p_b_c_b_l, params.p_w_c_n]);

    // --- front wall set    
    useMemo(() => {
        let tempWallCloseState;
        if (params.p_b_c_b_l_f === "Gable") {
            if (params.p_r_s === 1) {
                tempWallCloseState = params.lean_p_h + 0.1;
            } else {
                tempWallCloseState = params.lean_p_h - 0.2;
            }
        } else if (params.p_b_c_b_l_f === "One_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l_f === "Half_Close") {
            tempWallCloseState = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l_f === "Three_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l_f === "Half Dutch End") {
            tempWallCloseState = 0;
        } else if (params.p_b_c_b_l_f === "Half End") {
            tempWallCloseState = 0;
        } else if (params.p_b_c_b_l_f === "Close") {
            tempWallCloseState = 0;
        } else {
            tempWallCloseState = -1;
        }

        // if(params.p_w_c_n && params.p_b_c_b_l_f == "Close") tempWallCloseState = 3;

        setFrontWallCloseState(tempWallCloseState);
    }, [params.p_b_c_b_l_f, params.lean_p_h, params.p_r_s, params.p_w_c_n])

    // --- back wall set    
    useMemo(() => {
        let tempWallCloseState;

        if (params.p_b_c_b_l_b === "Gable") {
            if (params.p_r_s === 1) {
                tempWallCloseState = params.lean_p_h + 0.1;
            } else {
                tempWallCloseState = params.lean_p_h - 0.2;
            }
        } else if (params.p_b_c_b_l_b === "One_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l_b === "Half_Close") {
            tempWallCloseState = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l_b === "Three_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l_b === "Close") {
            tempWallCloseState = 0;
        } else if (params.p_b_c_b_l_b === "Half Dutch End") {
            tempWallCloseState = 0;
        } else if (params.p_b_c_b_l_b === "Half End") {
            tempWallCloseState = 0;
        } else {
            tempWallCloseState = -1;
        }
        // if(params.p_w_c_n && params.p_b_c_b_l_b == "Close") tempWallCloseState = 3;
        setBackWallCloseState(tempWallCloseState);
    }, [params.add_storage_check, params.p_b_c_b_l_b, params.lean_p_h, params.p_r_s, params.p_w_c_n])

    // --- side wall set    
    useMemo(() => {
        let tempWallCloseState = 0;
        if (params.p_b_c_b_l === 'Open' || typeof(params.p_b_c_b_l) === 'object') {
            if (params.p_r_s === 1) {
                tempWallCloseState = params.lean_p_h + 0.1;
            } else {
                tempWallCloseState = params.lean_p_h;
            }
        } else if (params.p_b_c_b_l === "One_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l === "Half_Close") {
            tempWallCloseState = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l === "Three_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l === "Close") {
            tempWallCloseState = 0;
        } else {
            tempWallCloseState = params.lean_p_h - 3 * params.p_b_c_b_l;
        }

        // if(params.p_w_c_n && params.p_b_c_b_l == "Close") tempWallCloseState = 3;

        setSideWallCloseState(tempWallCloseState);
    }, [params.p_b_c_b_l, params.lean_p_h, params.p_r_s, params.p_w_c_n])
    
    // --- porch state
    useMemo(() => {
        if (params.add_left_front_lean_porch) {
            setSideWallLeftPorchSize(params.lean_p_w);
        } else {
            setSideWallLeftPorchSize(0);
        }
    }, [params.add_left_front_lean_porch, params.lean_p_w, params.p_r_s])

    useMemo(() => {
        if (params.add_left_back_lean_porch) {
            setSideWallRightPorchSize(params.lean_p_w);
        } else {
            setSideWallRightPorchSize(0);
        }
    }, [params.add_left_back_lean_porch, params.lean_p_w, params.p_r_s])
    
    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "leftlean" && (params.activeWall === "L_L_F_S_W" || params.activeWall === "L_L_B_S_W"))) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);

    // useMemo(() => {
    //     let array = const_var.newDoorsArray.filter((item) => item.userData.wallName === 'c_b_l_w' && item.height > params.lean_p_h);
    //     for(let i=0; i<array.length; i++) {
    //         let item = array[i];
    //         const doorPositionX = item.positionX;
    //         const doorWidth = item.width
    //         const LeftStoragePos = params.lean_p_pos - params.lean_p_d / 2
    //         const RightStoragePos = params.lean_p_pos - params.lean_p_d / 2 + parseInt(params.add_storage)
    //         if((LeftStoragePos >= (doorPositionX - doorWidth / 2)) && (RightStoragePos <= (doorPositionX + doorWidth / 2))){
    //             setIsStorageRightWall(true)
    //             return;
    //         } 
    //         setIsStorageRightWall(false)
    //     }

    // }, [const_var.newDoorsArray, params.lean_p_pos, params.lean_p_d, params.add_storage, params.lean_p_h])

    return (
        <>
            <group {...eventHandlers}>
                {(params.p_b_c_b_l !== 'Open') && sideWallModelObj?.outerModel &&
                    <mesh name="L_L_L_W" geometry={sideWallModelObj?.outerModel} renderOrder={1}>
                        <MaterialOuterWallNew isPattern={isPattern} visibleSection={"leftlean"} />
                    </mesh>
                }
                {params.p_w_c_n == true && (params.p_b_c_b_l === "Close") && sideWallModelObj?.wainscotModel &&
                    <mesh name="L_L_L_W_wainscot" geometry={sideWallModelObj?.wainscotModel} position={[-0.005,0,0]} renderOrder={1}>
                        <MaterialWainScotWallNew isPattern={isPattern} visibleSection={"leftlean"} />
                    </mesh>
                }
                {(params.p_b_c_b_l !== 'Open') && sideWallModelObj && sideWallModelObj?.innerModel && 
                    <mesh name="side" geometry={sideWallModelObj?.innerModel} position={[wallThickness + 0.01, 0, 0]}>
                        <MaterialInnerWall visibleSection={"leftLean"} />
                    </mesh>
                }
            </group>
            {params.p_b_c_b_l_f !== 'Open' && frontWallModelObj?.outerModel &&
                <mesh name="L_L_F_W" geometry={frontWallModelObj?.outerModel} renderOrder={1}>
                    <MaterialOuterWallNew isPattern={isPattern} visibleSection={"leftlean"} />
                </mesh>
            }
            {params.p_w_c_n == true && (params.p_b_c_b_l_f === "Close" || params.p_b_c_b_l_f === "Half Dutch End" || params.p_b_c_b_l_f === "Half End") && frontWallModelObj?.wainscotModel &&
                <mesh name="L_L_F_W_wainscot" geometry={frontWallModelObj?.wainscotModel} position={[0,0,0.005]} renderOrder={1}>
                    <MaterialWainScotWallNew isPattern={isPattern} visibleSection={"leftlean"} />
                </mesh>
            }
            {
                frontWallModelObj && frontWallModelObj?.innerModel && <mesh name="lean_front_leg_wall" geometry={frontWallModelObj?.innerModel} position={[0, 0, -wallThickness * 3 - 0.01]}>
                    <MaterialInnerWall visibleSection={"leftLean"} />
                </mesh>
            }

            {params.p_b_c_b_l_b !== 'Open' && backWallModelObj?.outerModel &&
                <mesh name="L_L_B_W" geometry={backWallModelObj?.outerModel} renderOrder={1}>
                    <MaterialOuterWallNew isPattern={isPattern} visibleSection={"leftlean"} />
                </mesh>
            }
            {params.p_w_c_n == true && (params.p_b_c_b_l_b === "Close" || params.p_b_c_b_l_b === "Half Dutch End" || params.p_b_c_b_l_b === "Half End") && backWallModelObj?.wainscotModel &&
                <mesh name="L_L_B_W_wainscot" geometry={backWallModelObj?.wainscotModel} position={[0,0,-0.005]} renderOrder={1}>
                    <MaterialWainScotWallNew isPattern={isPattern} visibleSection={"leftlean"} />
                </mesh>
            }
            {
                backWallModelObj && backWallModelObj?.innerModel && <mesh name="lean_back_leg_wall" geometry={backWallModelObj?.innerModel} position={[0, 0, wallThickness + 0.01]}>
                    <MaterialInnerWall visibleSection={"leftLean"} />
                </mesh>
            }
        </>
    )
}

export default WallModel